// src/services/api.js
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://funnki.in/api/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor for adding token and Basic Auth
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        
        // Apply Basic Auth to any GET request if credentials are provided
        if (config.method?.toLowerCase() === 'get') {
            const basicUser = process.env.REACT_APP_BASIC_AUTH_USER || localStorage.getItem('basic_auth_user');
            const basicPass = process.env.REACT_APP_BASIC_AUTH_PASS || localStorage.getItem('basic_auth_pass');
            if (basicUser && basicPass) {
                config.headers.Authorization = 'Basic ' + btoa(`${basicUser}:${basicPass}`);
            } else if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        } else if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Product Services
export const productService = {
    // Get all products with filters
    getAllProducts: async (params = {}) => {
        try {
            const { page = 1, limit = 20, search = '', sort = 'newest', category = '', flag = '' } = params;
            let url = `/products?page=${page}&limit=${limit}&search=${search}&sort=${sort}`;
            if (category) url += `&category=${category}`;
            if (flag) url += `&flag=${flag}`;
            const response = await api.get(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching products:', error);
            throw error;
        }
    },
    getAllSizes: async () => {
        try {
            const response = await api.get('/products/sizes/all');
            return response.data;
        } catch (error) {
            console.error('Error fetching sizes:', error);
            throw error;
        }
    },

    // Get all available colors
    getAllColors: async () => {
        try {
            const response = await api.get('/products/colors/all');
            return response.data;
        } catch (error) {
            console.error('Error fetching colors:', error);
            throw error;
        }
    },
    // Get products (alias for getAllProducts)
    getProducts: async (params = {}) => {
        return productService.getAllProducts(params);
    },

    // Get single product by ID
    getProductById: async (id) => {
        try {
            const response = await api.get(`/products/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching product:', error);
            throw error;
        }
    },

    // Get products by flag (best_selling, trending, hot, new)
    getProductsByFlag: async (flag, limit = 10) => {
        try {
            const response = await api.get(`/products/flag/${flag}?limit=${limit}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching ${flag} products:`, error);
            throw error;
        }
    },

    // Get best selling products
    getBestSelling: async (limit = 10) => {
        try {
            const response = await api.get(`/products/best-selling?limit=${limit}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching best selling products:', error);
            throw error;
        }
    },

    // Get trending products
    getTrending: async (limit = 10) => {
        try {
            const response = await api.get(`/products/trending?limit=${limit}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching trending products:', error);
            throw error;
        }
    },

    // Get new arrivals
    getNewArrivals: async (limit = 10) => {
        try {
            const response = await api.get(`/products/new-arrivals?limit=${limit}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching new arrivals:', error);
            throw error;
        }
    },

    // Get featured products
    getFeatured: async (limit = 10) => {
        try {
            const response = await api.get(`/products/featured?limit=${limit}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching featured products:', error);
            throw error;
        }
    },

    // Create product (admin)
    createProduct: async (formData) => {
        try {
            const response = await api.post('/products', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            console.error('Error creating product:', error);
            throw error;
        }
    },

    // Update product (admin)
    updateProduct: async (id, formData) => {
        try {
            const response = await api.put(`/products/${id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            console.error('Error updating product:', error);
            throw error;
        }
    },

    // Delete product (admin)
    deleteProduct: async (id) => {
        try {
            const response = await api.delete(`/products/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting product:', error);
            throw error;
        }
    }
};

// Category Services
export const categoryService = {
    // Get all main categories
    getMainCategories: async () => {
        try {
            const response = await api.get('/categories/main-categories');
            return response.data;
        } catch (error) {
            console.error('Error fetching main categories:', error);
            throw error;
        }
    },

    // Get subcategories by main category
    getSubcategories: async (mainCategoryId) => {
        try {
            const response = await api.get(`/categories/subcategories/${mainCategoryId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching subcategories:', error);
            throw error;
        }
    },

    // Get child categories by subcategory
    getChildCategories: async (subcategoryId) => {
        try {
            const response = await api.get(`/categories/child-categories/${subcategoryId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching child categories:', error);
            throw error;
        }
    },

    // Get full category hierarchy
    getFullHierarchy: async () => {
        try {
            const response = await api.get('/categories/full-hierarchy');
            return response.data;
        } catch (error) {
            console.error('Error fetching category hierarchy:', error);
            throw error;
        }
    }
};


export const orderService = {
    // Get my orders
    getMyOrders: async () => {
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('guest_token');
            const response = await api.get('/orders/my-orders', {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching orders:', error);
            throw error;
        }
    },

    // Get single order
    getOrderById: async (id) => {
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('guest_token');
            const response = await api.get(`/orders/order/${id}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching order:', error);
            throw error;
        }
    },

    // Track order by number
    trackOrder: async (orderNumber) => {
        try {
            const response = await api.get(`/orders/track/${orderNumber}`);
            return response.data;
        } catch (error) {
            console.error('Error tracking order:', error);
            throw error;
        }
    },

    // Create order
    createOrder: async (orderData) => {
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('guest_token');
            const response = await api.post('/orders/checkout', orderData, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            return response.data;
        } catch (error) {
            console.error('Error creating order:', error);
            throw error;
        }
    },

    // Cancel order
    cancelOrder: async (id) => {
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('guest_token');
            const response = await api.post(`/orders/${id}/cancel`, {}, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            return response.data;
        } catch (error) {
            console.error('Error cancelling order:', error);
            throw error;
        }
    }
};

export const paymentService = {
    createRazorpayOrder: async (amount, currency = 'INR', receipt = null) => {
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('guest_token');
            const response = await api.post('/payments/create-razorpay-order', 
                { amount, currency, receipt },
                { headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );
            return response.data;
        } catch (error) {
            console.error('Create Razorpay order error:', error);
            throw error;
        }
    },
    
    verifyPayment: async (paymentData) => {
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('guest_token');
            console.log('🔐 Verifying payment with data:', paymentData);
            
            const response = await api.post('/payments/verify-payment', paymentData, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            
            return response.data;
        } catch (error) {
            console.error('Verify payment error:', error);
            console.error('Error response:', error.response?.data);
            throw error;
        }
    }
};
// Cart Services
export const cartService = {
    // Get cart
    getCart: async () => {
        try {
            const response = await api.get('/cart');
            return response.data;
        } catch (error) {
            console.error('Error fetching cart:', error);
            throw error;
        }
    },

    // Add to cart
    addToCart: async (item) => {
        try {
            const response = await api.post('/cart/add', item);
            return response.data;
        } catch (error) {
            console.error('Error adding to cart:', error);
            throw error;
        }
    },

    // Update cart item
    updateCartItem: async (id, quantity) => {
        try {
            const response = await api.put(`/cart/update/${id}`, { quantity });
            return response.data;
        } catch (error) {
            console.error('Error updating cart:', error);
            throw error;
        }
    },

    // Remove from cart
    removeFromCart: async (id) => {
        try {
            const response = await api.delete(`/cart/remove/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error removing from cart:', error);
            throw error;
        }
    },

    // Clear cart
    clearCart: async () => {
        try {
            const response = await api.delete('/cart/clear');
            return response.data;
        } catch (error) {
            console.error('Error clearing cart:', error);
            throw error;
        }
    }
};

// services/api.js
export const authService = {
    // ==================== USER REGISTRATION (OTP) ====================
    sendRegistrationOtp: async (name, email, phone) => {
        try {
            const response = await api.post('/auth/register/send-otp', { name, email, phone });
            return response.data;
        } catch (error) {
            console.error('Send registration OTP error:', error);
            throw error;
        }
    },
    
    verifyRegistrationOtp: async (email, otp) => {
        try {
            const response = await api.post('/auth/register/verify', { email, otp });
            return response.data;
        } catch (error) {
            console.error('Verify registration OTP error:', error);
            throw error;
        }
    },
    
    // ==================== USER LOGIN (OTP - No Password) ====================
    sendUserLoginOtp: async (email) => {
        try {
            const response = await api.post('/auth/user/login/send-otp', { email });
            return response.data;
        } catch (error) {
            console.error('Send user login OTP error:', error);
            throw error;
        }
    },
    
    verifyUserLoginOtp: async (email, otp) => {
        try {
            const response = await api.post('/auth/user/login/verify', { email, otp });
            return response.data;
        } catch (error) {
            console.error('Verify user login OTP error:', error);
            throw error;
        }
    },

    // ==================== GUEST / QUICK CHECKOUT OTP ====================
    sendGuestOtp: async (data) => {
        try {
            const response = await api.post('/auth/user/login/send-otp', data);
            return response.data;
        } catch (error) {
            console.error('Send guest OTP error:', error);
            throw error;
        }
    },
    
    verifyGuestOtp: async (data) => {
        try {
            const response = await api.post('/auth/user/login/verify', data);
            return response.data;
        } catch (error) {
            console.error('Verify guest OTP error:', error);
            throw error;
        }
    },
    
    // ==================== ADMIN LOGIN (Password) ====================
    adminLogin: async (email, password) => {
        try {
            const response = await api.post('/auth/admin/login', { email, password });
            return response.data;
        } catch (error) {
            console.error('Admin login error:', error);
            throw error;
        }
    },
    
    // ==================== PROFILE ====================
    getProfile: async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('/auth/profile', {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Get profile error:', error);
            throw error;
        }
    },
    
    updateProfile: async (profileData) => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.put('/auth/profile', profileData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Update profile error:', error);
            throw error;
        }
    },
    
    // ==================== LOGOUT ====================
    logout: async () => {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                await api.post('/auth/logout', {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
        }
    }
};
// Add to your existing productService or create wishlistService
export const wishlistService = {
    // Get user's wishlist
    getWishlist: async () => {
        try {
            const token = localStorage.getItem('token');
            
            // If no token, use localStorage for guest
            if (!token) {
                const localWishlist = JSON.parse(localStorage.getItem('guest_wishlist') || '[]');
                return { 
                    success: true, 
                    wishlist: localWishlist,
                    count: localWishlist.length,
                    isGuest: true 
                };
            }
            
            const response = await api.get(`/wishlist`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                return response.data;
            }
            
            return { success: true, wishlist: [] };
        } catch (error) {
            console.error('Get wishlist error:', error);
            const localWishlist = JSON.parse(localStorage.getItem('guest_wishlist') || '[]');
            return { success: true, wishlist: localWishlist };
        }
    },

    // Add product to wishlist
    addToWishlist: async (product) => {
        try {
            const token = localStorage.getItem('token');
            
            // Handle guest wishlist in localStorage
            if (!token) {
                const localWishlist = JSON.parse(localStorage.getItem('guest_wishlist') || '[]');
                if (!localWishlist.some(item => item.id === product.id)) {
                    localWishlist.push(product);
                    localStorage.setItem('guest_wishlist', JSON.stringify(localWishlist));
                }
                return { 
                    success: true, 
                    message: 'Added to wishlist',
                    product: product,
                    count: localWishlist.length,
                    isGuest: true
                };
            }
            
            const response = await api.post(`/wishlist/add`, 
                { product_id: product.id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (response.data.success) {
                return { 
                    success: true, 
                    message: 'Added to wishlist',
                    product: product
                };
            }
            
            return response.data;
        } catch (error) {
            console.error('Add to wishlist error:', error);
            return { success: false, message: error.response?.data?.message || 'Failed to add to wishlist' };
        }
    },

    // Remove product from wishlist
    removeFromWishlist: async (productId) => {
        try {
            const token = localStorage.getItem('token');
            
            // Handle guest wishlist in localStorage
            if (!token) {
                const localWishlist = JSON.parse(localStorage.getItem('guest_wishlist') || '[]');
                const updated = localWishlist.filter(item => item.id !== productId);
                localStorage.setItem('guest_wishlist', JSON.stringify(updated));
                return { 
                    success: true, 
                    message: 'Removed from wishlist',
                    count: updated.length,
                    isGuest: true 
                };
            }
            
            const response = await api.delete(`/wishlist/remove/${productId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Remove from wishlist error:', error);
            return { success: false, message: error.response?.data?.message || 'Failed to remove from wishlist' };
        }
    },

    // Clear wishlist
    clearWishlist: async () => {
        try {
            const token = localStorage.getItem('token');
            
            if (!token) {
                localStorage.removeItem('guest_wishlist');
                return { success: true };
            }
            
            const response = await api.delete(`/wishlist/clear`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Clear wishlist error:', error);
            return { success: false };
        }
    }
};
// User Services (Admin)
export const userService = {
    // Get all users (admin)
    getUsers: async (params = {}) => {
        try {
            const { page = 1, limit = 10, search = '' } = params;
            const response = await api.get(`/users?page=${page}&limit=${limit}&search=${search}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching users:', error);
            throw error;
        }
    },

    // Get single user (admin)
    getUserById: async (id) => {
        try {
            const response = await api.get(`/users/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching user:', error);
            throw error;
        }
    },

    // Update user (admin)
    updateUser: async (id, userData) => {
        try {
            const response = await api.put(`/users/${id}`, userData);
            return response.data;
        } catch (error) {
            console.error('Error updating user:', error);
            throw error;
        }
    },

    // Delete user (admin)
    deleteUser: async (id) => {
        try {
            const response = await api.delete(`/users/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting user:', error);
            throw error;
        }
    }
};
export const returnService = {
    // Get user's returns
    getMyReturns: async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('/returns/my-returns', {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Get returns error:', error);
            throw error;
        }
    },
    
    // Create return request
    createReturn: async (returnData) => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/returns/create', returnData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Create return error:', error);
            throw error;
        }
    },
    
    // Get return details
    getReturnDetails: async (returnId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get(`/returns/${returnId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Get return details error:', error);
            throw error;
        }
    },
    
    // Cancel return request
    cancelReturn: async (returnId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.put(`/returns/cancel/${returnId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Cancel return error:', error);
            throw error;
        }
    }
};
// Dashboard Services (Admin)
export const dashboardService = {
    // Get dashboard stats
    getStats: async () => {
        try {
            const response = await api.get('/dashboard/stats');
            return response.data;
        } catch (error) {
            console.error('Error fetching stats:', error);
            throw error;
        }
    },

    // Get sales chart data
    getSalesChart: async () => {
        try {
            const response = await api.get('/dashboard/sales-chart');
            return response.data;
        } catch (error) {
            console.error('Error fetching sales chart:', error);
            throw error;
        }
    },

    // Get top products
    getTopProducts: async () => {
        try {
            const response = await api.get('/dashboard/top-products');
            return response.data;
        } catch (error) {
            console.error('Error fetching top products:', error);
            throw error;
        }
    },

    // Get all orders (admin)
    getAllOrders: async (params = {}) => {
        try {
            const { page = 1, limit = 20, status = '' } = params;
            const response = await api.get(`/dashboard/orders?page=${page}&limit=${limit}&status=${status}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching orders:', error);
            throw error;
        }
    },

    // Update order status (admin)
    updateOrderStatus: async (id, status, trackingNumber) => {
        try {
            const response = await api.put(`/orders/${id}/status`, { status, tracking_number: trackingNumber });
            return response.data;
        } catch (error) {
            console.error('Error updating order status:', error);
            throw error;
        }
    }
};
// Address Services
export const addressService = {
    // Get user addresses
    getAddresses: async () => {
        try {
            const response = await api.get('/addresses');
            return response.data;
        } catch (error) {
            console.error('Error fetching addresses:', error);
            throw error;
        }
    },
    
    // Add new address
    addAddress: async (addressData) => {
        try {
            const response = await api.post('/addresses', addressData);
            return response.data;
        } catch (error) {
            console.error('Error adding address:', error);
            throw error;
        }
    },
    
    // Update address
    updateAddress: async (id, addressData) => {
        try {
            const response = await api.put(`/addresses/${id}`, addressData);
            return response.data;
        } catch (error) {
            console.error('Error updating address:', error);
            throw error;
        }
    },
    
    // Delete address
    deleteAddress: async (id) => {
        try {
            const response = await api.delete(`/addresses/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting address:', error);
            throw error;
        }
    }
};

// Contact Services
export const contactService = {
    sendMessage: async (formData) => {
        try {
            const response = await api.post('/contact', formData);
            return response.data;
        } catch (error) {
            console.error('Error sending contact message:', error);
            throw error?.response?.data || error;
        }
    }
};

// Helper to dynamically set Basic Auth credentials for GET API calls
export const setBasicAuth = (username, password) => {
    localStorage.setItem('basic_auth_user', username);
    localStorage.setItem('basic_auth_pass', password);
};

export default api;